import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldValues, Path, useFormContext } from 'react-hook-form';

import { Button, Checkbox, TIconName } from '@treezor/design-system';

import { CustomPopover } from './CustomPopover';

export interface IFilterOptions<T> {
  title?: string;
  options: { label: string; name: Path<T>; value: string }[];
}

interface ICheckboxesFilterProps<T> {
  popoverOptions: {
    label: string;
    icon?: TIconName;
  };
  filtersOptions: IFilterOptions<T>[];
  name: Path<T>;
  onFilterChange: (filters: { [key: string]: string }) => void;
  columns?: number;
}

export const CheckboxesFilter = <T extends FieldValues>({
  popoverOptions: { label, icon },
  name,
  filtersOptions,
  onFilterChange,
  columns,
}: ICheckboxesFilterProps<T>) => {
  const { t } = useTranslation();

  const { setValue, getValues, watch } = useFormContext<T>();

  const checkboxesOptions = filtersOptions.map(({ options }) => options).flat();

  const handleCheckOption = useCallback(() => {
    const options = Object.values(checkboxesOptions).reduce((acc: string, status) => {
      const formValue = getValues(status.name);
      if (formValue) {
        return acc.length ? `${acc},${status.value}` : status.value;
      }
      return acc;
    }, '') as T[string];
    const transactionsFilters = getValues();
    setValue(name, options);
    onFilterChange({ ...transactionsFilters, [name]: options });
  }, [getValues, setValue, checkboxesOptions, onFilterChange, name]);

  const handleReset = useCallback(() => {
    checkboxesOptions.forEach(({ name }) => {
      setValue(name, false as T[string]);
    });
    handleCheckOption();
  }, [handleCheckOption, checkboxesOptions, setValue]);

  const handleSelectAll = useCallback(() => {
    checkboxesOptions.forEach(({ name }) => {
      setValue(name, true as T[string]);
    });
    handleCheckOption();
  }, [handleCheckOption, checkboxesOptions, setValue]);

  return (
    <CustomPopover
      id={name}
      label={label}
      icon={icon as TIconName}
      content={
        <div className="flex flex-col items-start m-6 gap-5">
          {filtersOptions.map(({ title, options }, index) => (
            <div key={`sub-menu-${index}`}>
              {title && <span className="typo-body-regular-small text-secondary">{title}</span>}
              <div className="grid gap-3 mt-2" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
                {options.map(({ label: checkboxLabel, name: checkboxName }) => (
                  <Checkbox
                    key={checkboxName}
                    name={checkboxName}
                    label={checkboxLabel}
                    checked={watch(checkboxName) === true}
                    onChange={(e) => {
                      setValue(checkboxName, e.currentTarget.checked as T[string]);
                      handleCheckOption();
                    }}
                    data-test-id={`${name}-${checkboxName.toLowerCase()}`}
                  />
                ))}
              </div>
            </div>
          ))}
          <div className="flex justify-between w-full min-w-[7rem]">
            <Button id="direction-filter-select-all-btn" onClick={handleSelectAll} size="s" variant="ghost">
              {t('pages.cores.selectAll')}
            </Button>
            <Button id="direction-filter-reset-btn" onClick={handleReset} size="s" variant="ghost">
              {t('pages.cores.reset')}
            </Button>
          </div>
        </div>
      }
      indication={getValues(name) && typeof getValues(name) === 'string' ? getValues(name).split(',').length : 0}
    />
  );
};
